/**
 * DistributeChannels — co-located subcomponents for the distribution one-pager
 * (/genies/:id/distribute). Rebuilds the wireframe's channel blocks on-brand
 * around the REAL DistributionCard modals (Phone / Web embed / QR / Share).
 *
 * Brand law: genie-* tokens only; signature gradient for hero accents; wireframe
 * purple -> genie-secondary, green (Live/Active) -> genie-success. GenieIcon
 * for chrome, Lucide inline icons only.
 *
 * Prefix Distribute* so parallel workers never collide.
 */
import type { ReactNode } from "react";
import { Bell, Check, ChevronRight, Copy, ExternalLink, Phone } from "lucide-react";
import { Pill } from "@/components/voice-genie/atoms";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

/* ─────────────── Hero share bar ─────────────── */

interface DistributeShareBarProps {
  url: string;
  copied: boolean;
  onOpen: () => void;
  onCopy: () => void;
}

/** Gradient-bordered hero: the public live page URL + Open + Copy. */
export const DistributeShareBar = ({ url, copied, onOpen, onCopy }: DistributeShareBarProps) => (
  <div className="rounded-2xl p-[1.5px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-xl shadow-genie-primary/20">
    <div className="rounded-[14px] bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
        <GenieIcon name="share" size={22} color="#ffffff" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-[0.6px] text-gray-500">
          Your genie&rsquo;s page
        </p>
        <p className="text-base font-bold text-[#222B45] truncate">{url}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={onOpen}
          className="h-10 px-3.5 rounded-xl bg-[#F9FAFC] border border-genie-border flex items-center gap-2 text-[13px] font-semibold text-[#64748B] hover:bg-gray-50 transition-colors"
        >
          <ExternalLink className="w-[15px] h-[15px]" />
          Open
        </button>
        <button
          type="button"
          onClick={onCopy}
          className="h-10 px-4 rounded-2xl flex items-center gap-2 text-[13px] font-semibold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 hover:opacity-90 transition-all"
        >
          {copied ? <Check className="w-[15px] h-[15px]" /> : <Copy className="w-[15px] h-[15px]" />}
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  </div>
);

/* ─────────────── Channel card ─────────────── */

interface DistributeChannelCardProps {
  genieIcon: GenieIconName;
  tint: string;
  title: string;
  description: string;
  status: string;
  statusLive?: boolean;
  /** Action button label */
  action: string;
  /** Primary (gradient) action vs neutral outline */
  actionPrimary?: boolean;
  onAction: () => void;
  children?: ReactNode;
}

/** A single distribution channel: icon chip + title + status pill + preview slot + action. */
export const DistributeChannelCard = ({
  genieIcon,
  tint,
  title,
  description,
  status,
  statusLive,
  action,
  actionPrimary,
  onAction,
  children,
}: DistributeChannelCardProps) => (
  <div className="bg-white rounded-2xl border border-genie-border shadow-sm p-4 flex flex-col gap-3 h-full">
    <div className="flex items-start gap-3">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${tint}1a` }}
      >
        <GenieIcon name={genieIcon} size={19} color={tint} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[14.5px] font-bold text-[#222B45] leading-tight">{title}</h3>
        <p className="text-[12.5px] text-gray-500 leading-snug mt-0.5">{description}</p>
      </div>
      <Pill tone={statusLive ? "success" : "neutral"}>
        <span className="inline-flex items-center gap-1.5">
          <span
            className={`w-1.5 h-1.5 rounded-full ${statusLive ? "bg-genie-success" : "bg-gray-400"}`}
          />
          {status}
        </span>
      </Pill>
    </div>

    {children}

    <button
      type="button"
      onClick={onAction}
      className={`mt-auto h-[38px] rounded-xl flex items-center justify-center gap-2 text-[13px] font-bold transition-all ${
        actionPrimary
          ? "text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/25 hover:opacity-90"
          : "text-[#222B45] bg-[#F9FAFC] border border-genie-border hover:bg-gray-50"
      }`}
    >
      {action}
    </button>
  </div>
);

/* ─────────────── Phone preview slot ─────────────── */

export const DistributePhonePreview = ({ number }: { number: string }) => (
  <div className="h-10 rounded-lg bg-[#F9FAFC] border border-genie-border flex items-center gap-2 px-3">
    <Phone className="w-[14px] h-[14px] text-gray-400" />
    <span className="text-sm font-semibold text-[#222B45] font-mono">{number}</span>
  </div>
);

/* ─────────────── Embed snippet slot ─────────────── */

export const DistributeEmbedPreview = ({ urlName }: { urlName: string }) => (
  <div className="h-10 rounded-lg bg-[#0B0C18] flex items-center gap-2 px-3 overflow-hidden">
    <span className="text-[#64748B] font-mono text-xs flex-shrink-0">&lt;/&gt;</span>
    <span className="text-[#94A3B8] font-mono text-xs whitespace-nowrap truncate">
      {`<script src="helpgenie.ai/embed.js" data-genie="${urlName}" …>`}
    </span>
  </div>
);

/* ─────────────── Email signature slot ─────────────── */

export const DistributeSignaturePreview = ({ owner }: { owner: string }) => (
  <div className="h-10 rounded-lg bg-[#F9FAFC] border border-genie-border flex items-center justify-between gap-2 pl-3 pr-2">
    <span className="text-[12.5px] text-[#64748B] truncate">{owner}</span>
    <span className="h-6 px-2.5 rounded-full flex items-center gap-1 flex-shrink-0 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
      <Phone className="w-[11px] h-[11px] text-white" />
      <span className="text-[10.5px] font-bold text-white">Talk to our genie</span>
    </span>
  </div>
);

/* ─────────────── Mobile app slot ─────────────── */

/**
 * Preview for the "in their Help Genie app" channel: a phone silhouette with
 * this genie sitting in My Genies, which is exactly what the /keep link does
 * to the recipient's app.
 */
export const DistributeAppPreview = ({ genieName }: { genieName: string }) => (
  <div className="h-10 rounded-lg bg-[#F9FAFC] border border-genie-border flex items-center gap-2.5 px-2.5">
    <span className="w-[22px] h-[30px] rounded-[5px] border-[1.5px] border-[#CBD5E1] flex items-start justify-center pt-[3px] flex-shrink-0">
      <span className="w-2.5 h-[3px] rounded-full bg-[#CBD5E1]" />
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-[10px] font-bold uppercase tracking-[0.6px] text-[#94A3B8] leading-none">
        My Genies
      </span>
      <span className="block text-[12.5px] font-semibold text-[#222B45] truncate leading-tight mt-0.5">
        {genieName}
      </span>
    </span>
  </div>
);

/* ─────────────── Notifications row (full width) ─────────────── */

interface DistributeNotificationsRowProps {
  enabled: boolean;
  onToggle: () => void;
}

export const DistributeNotificationsRow = ({ enabled, onToggle }: DistributeNotificationsRowProps) => (
  <div className="bg-white rounded-2xl border border-genie-border shadow-sm p-4 flex items-center gap-4">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-genie-accent/15">
      <Bell className="w-[19px] h-[19px] text-genie-accent-dark" />
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="text-[14.5px] font-bold text-[#222B45]">Notifications</h3>
      <p className="text-[12.5px] text-gray-500">
        Get a summary, lead or alert the moment your genie finishes a call.
      </p>
    </div>
    <span className="hidden sm:block text-[12.5px] text-[#64748B] flex-shrink-0">Email + push</span>
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label="Toggle call notifications"
      onClick={onToggle}
      className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors ${
        enabled ? "bg-genie-primary" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-all ${
          enabled ? "right-[3px]" : "left-[3px]"
        }`}
      />
    </button>
  </div>
);

/* ─────────────── Activation pack banner ─────────────── */

export const DistributeActivationPack = ({ onOpen }: { onOpen: () => void }) => (
  <button
    type="button"
    onClick={onOpen}
    className="group w-full text-left rounded-2xl overflow-hidden bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-xl shadow-genie-secondary/25 hover:-translate-y-0.5 transition-transform"
  >
    <div className="p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/20">
        <GenieIcon name="genie-lamp" size={26} color="#ffffff" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[17px] font-extrabold text-white tracking-[-0.3px]">Activation pack</h3>
        <p className="text-[13px] text-white/80 mt-0.5 leading-snug">
          Everything bundled to launch today. Share link, branded QR, website embed, ready to post
          socials and a printable one pager.
        </p>
      </div>
      <span className="hidden sm:flex h-[42px] px-4 rounded-xl items-center gap-2 flex-shrink-0 bg-white text-[13.5px] font-bold text-genie-primary">
        Open activation pack
        <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </div>
  </button>
);
